import {
  Alert,
  Box,
  Button,
  Center,
  Text,
  Tabs,
  Group,
  TextInput,
  Menu,
  Loader,
} from '@mantine/core';
// import { Welcome } from '../components/Welcome/Welcome';  <Welcome />
import { pageWrapper } from '@/Styles';
import { getJson } from '@/services/httpClient';
import { MarketplaceTabLabel } from '@/components/MarketplaceTabLabel';
import { PageHeader } from '@/components/PageHeader';
import { clientRoutes, websiteDomain, layoutPath } from '../ApiPaths';
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { IProduct } from '@/types/Product.model';
import { UserProfile, useStore } from '@/store/Store';
import { getAllProducts, getTagsForFilter /* , getCategoriesForFilter */ } from '@/Api';
// Temporarily hidden — categories UI disabled
// import { PRODUCT_CATEGORIES } from '@/constants/productCategories';
import {
  IconPhoto,
  IconUsers,
  IconStar,
  IconStarFilled,
  IconCheckbox,
  IconSearch,
  IconHome2,
  IconSortAscending,
  IconSortDescending,
  IconArrowsSort,
  IconSelector,
  IconCheck,
  IconAlertCircle,
  IconRefresh,
} from '@tabler/icons-react';
import { isMobile } from 'is-mobile';
import { ProductCard } from '@/components/ProductCard';
import { VirtualizedProductCardGrid } from '@/components/VirtualizedProductCardGrid';
import type { ProductStarState } from '@/hooks/useProductStarToggle';
import { MarketplaceFiltersDropdown } from '@/components/MarketplaceFiltersDropdown';
import { MarketplaceToolbarButton } from '@/components/MarketplaceToolbarButton';
import { MarketplaceComingSoonPanel } from '@/components/MarketplaceComingSoonPanel';
import marketplaceTabStyles from '@/components/MarketplaceTabs.module.css';
import { getMarketplaceTabStyleVars } from '@/components/marketplaceTabStyleVars';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { capitalizeFirstLetter } from '@/utils/tags';
import { retryAsync } from '@/utils/retryAsync';

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px',
  borderRadius: '10px',
  backgroundColor: 'color-mix(in srgb, var(--mantine-color-default) 40%, transparent)',
};

const colStyle: React.CSSProperties = {
  ...rowStyle,
  alignItems: 'flex-start',
  flexDirection: 'column',
};

const tabStyle: React.CSSProperties = {
  marginTop: '30px',
  width: '100%',
  gap: '10px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '600px',
};

const marketStyles: Record<string, CSSProperties> = {
  description: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '4px',
    alignItems: 'center',
    gap: '4px',
    height: '200px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '300px',
    gap: '4px',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'color-mix(in srgb, var(--mantine-color-default) 40%, transparent)',
    padding: '10px 30px 10px 30px',
    borderRadius: '4px',
    position: 'relative',
  },
  views: {
    position: 'absolute',
    top: '64px',
    right: '36px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.5,
  },
  cardImage: {
    maxWidth: '120px',
    maxHeight: '120px',
    borderRadius: '10px',
  },
  row: {
    marginTop: '30px',
    maxWidth: '100%',
    gap: '36px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
};

// Ordering tier by company: everything else first, then robotop, then sellamensa last.
const companySortRank = (companyName?: string): number => {
  const name = companyName?.toLowerCase() ?? '';
  if (name.includes('sellamensa')) return 2;
  if (name.includes('robotop')) return 1;
  return 0;
};

const sortCompaniesBottom = (a: IPopulatedProduct, b: IPopulatedProduct) => {
  const rankA = companySortRank(a.owningUser_id.companyName);
  const rankB = companySortRank(b.owningUser_id.companyName);
  if (rankA !== rankB) return rankA - rankB;
  const aUser = a.owningUser_id.username;
  const bUser = b.owningUser_id.username;
  if (!aUser || !bUser || aUser == bUser) return 0;
  return aUser.localeCompare(bUser);
};

export interface IPopulatedProduct extends Partial<Omit<IProduct, 'owningUser_id'>> {
  owningUser_id: Partial<UserProfile>;
}

const modernMarketStyles = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: 'all 0.2s ease',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 8px 15px rgba(0, 0, 0, 0.1)',
    },
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: '8px 8px 0 0',
  },
  viewsBadge: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    padding: '2px 8px',
    borderRadius: '12px',
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: 'white',
  },
  contentSection: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flexGrow: 1,
  },
  productName: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.3,
    marginBottom: '8px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: '10px',
    borderTop: '1px solid #f0f0f0',
  },
  companyName: {
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  username: {
    fontSize: '0.8rem',
    color: '#666',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  grid: {
    width: '100%',
    marginTop: '20px',
  },
};

export function ProductLayoutGallery({
  layoutList,
  isLayout,
  onStarChange,
}: {
  layoutList: (IPopulatedProduct | any)[];
  isLayout: boolean;
  onStarChange?: (productId: string, state: ProductStarState) => void;
}) {
  const isMobileView = isMobile();

  return (
    <VirtualizedProductCardGrid
      items={layoutList}
      isMobileLayout={isMobileView}
      renderItem={(product) => (
        <ProductCard
          product={product}
          isLayout={isLayout}
          showArticleNumber={false}
          showListingStatus={true}
          showPrice={true}
          onStarChange={onStarChange}
          onClick={() => {
            window.location.href = isLayout
              ? `${websiteDomain}${clientRoutes.layout}`.replace(':id', product._id as string)
              : `${websiteDomain}${clientRoutes.product}`.replace(':id', product._id as string);
          }}
        />
      )}
    />
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const session = useSessionContext();
  const userProfile: UserProfile = useStore((state) => state.userProfile);
  const [productList, setProductList] = useState<IPopulatedProduct[]>([]);
  const [layoutList, setLayoutList] = useState<IPopulatedProduct[]>([]);
  const [filteredProductList, setFilteredProductList] = useState<IPopulatedProduct[] | null>(null);
  const [filteredLayoutList, setFilteredLayoutList] = useState<IPopulatedProduct[] | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    const raw = searchParams.get('tags');
    if (!raw?.trim()) return [];
    return raw
      .split(',')
      .map((s) => capitalizeFirstLetter(s.trim()))
      .filter(Boolean);
  });
  // Temporarily hidden — categories UI disabled
  // const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
  //   const raw = searchParams.get('categories');
  //   if (!raw?.trim()) return [];
  //   return raw
  //     .split(',')
  //     .map((s) => s.trim())
  //     .filter(Boolean);
  // });
  // const [allCategoriesList, setAllCategoriesList] = useState<string[]>([...PRODUCT_CATEGORIES]);
  // const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [allTagsList, setAllTagsList] = useState<string[]>([]);
  const [tagCounts, setTagCounts] = useState<Record<string, number>>({});
  const [sortOption, setSortOption] = useState<'none' | 'date-asc' | 'views-desc' | 'views-asc'>(
    'none'
  );
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const [products, setProducts] = useState<IPopulatedProduct[]>([]);
  const [layouts, setLayouts] = useState<IPopulatedProduct[]>([]);
  const [projects, setProjects] = useState<IPopulatedProduct[]>([]);
  const [experts, setExperts] = useState<IPopulatedProduct[]>([]);
  const [productsLoadState, setProductsLoadState] = useState<'loading' | 'ok' | 'error'>(
    'loading'
  );
  const [layoutsLoadState, setLayoutsLoadState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [loadRetryKey, setLoadRetryKey] = useState(0);

  /** Used so we refetch after login/session restore; otherwise first GET runs before cookies/session are ready and starredByMe is missing. */
  const sessionAuthKey = useMemo(() => {
    if (session.loading) return 'loading';
    return session.doesSessionExist ? 'user' : 'guest';
  }, [session]);

  // Deep link / navigation from product page: ?tags=a,b /* and ?categories=slug */
  const tagsQueryParam = searchParams.get('tags') ?? '';
  // Temporarily hidden — categories UI disabled
  // const categoriesQueryParam = searchParams.get('categories') ?? '';
  useEffect(() => {
    if (location.pathname !== clientRoutes.home) return;
    if (!tagsQueryParam.trim()) return;
    const next = tagsQueryParam
      .split(',')
      .map((s) => capitalizeFirstLetter(s.trim()))
      .filter(Boolean);
    setSelectedTags(next);
  }, [location.pathname, tagsQueryParam]);

  /* Temporarily hidden — categories UI disabled
  useEffect(() => {
    if (location.pathname !== clientRoutes.home) return;
    if (!categoriesQueryParam.trim()) return;
    const next = categoriesQueryParam
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setSelectedCategories(next);
  }, [location.pathname, categoriesQueryParam]);
  */

  // Fetch products with optional company and tag filters (server-side)
  // Temporarily hidden — categories UI disabled (categories omitted from filters)
  useEffect(() => {
    if (session.loading) return;
    let cancelled = false;
    setProductsLoadState('loading');
    const filters =
      selectedCompanies.length > 0 || selectedTags.length > 0
        ? {
            companies: selectedCompanies,
            tags: selectedTags,
            // categories: selectedCategories,
          }
        : undefined;

    retryAsync(() => getAllProducts(filters))
      .then((products) => {
        if (cancelled) return;
        setProductList((products as IPopulatedProduct[]).sort(sortCompaniesBottom));
        setProductsLoadState('ok');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setProductsLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedCompanies,
    selectedTags,
    // selectedCategories,
    session.loading,
    sessionAuthKey,
    location.pathname,
    loadRetryKey,
  ]);

  useEffect(() => {
    if (sessionAuthKey === 'guest') {
      setShowFavoritesOnly(false);
    }
  }, [sessionAuthKey]);

  useEffect(() => {
    let cancelled = false;
    setLayoutsLoadState('loading');
    retryAsync(() => getJson<IPopulatedProduct[]>(layoutPath))
      .then((data) => {
        if (cancelled) return;
        setLayoutList(data.sort(sortCompaniesBottom));
        setLayoutsLoadState('ok');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setLayoutsLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [loadRetryKey]);

  const retryMarketplaceLoad = useCallback(() => {
    setLoadRetryKey((k) => k + 1);
  }, []);
  // Tags for filter dropdown (+ categories temporarily disabled)
  useEffect(() => {
    if (location.pathname !== clientRoutes.home) return;
    getTagsForFilter()
      .then((r) => {
        setAllTagsList(
          [...new Set([...r.suggested, ...r.distinct])].sort((a, b) => a.localeCompare(b))
        );
        setTagCounts(r.tagCounts ?? {});
      })
      .catch(() => {});
    /* Temporarily hidden — categories UI disabled
    getCategoriesForFilter()
      .then((r) => {
        setAllCategoriesList(
          [...new Set([...r.suggested, ...r.distinct, ...PRODUCT_CATEGORIES])].sort((a, b) =>
            a.localeCompare(b)
          )
        );
        setCategoryCounts(r.categoryCounts ?? {});
      })
      .catch(() => {});
    */
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Apply the current width immediately. A resize listener alone misses the first paint.
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  //console.log(layoutList);

  // Unique company names from products and layouts (owningUser_id.companyName)
  const companyNames = useMemo(() => {
    const set = new Set<string>();
    const add = (list: IPopulatedProduct[]) =>
      list.forEach((p) => {
        const name = p.owningUser_id?.companyName;
        if (name && name.trim()) set.add(name.trim());
      });
    add(productList);
    add(layoutList);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [productList, layoutList]);

  useEffect(() => {
    // Apply text search only (company filter is applied when deriving display lists).
    // Empty search clears immediately. Typed queries wait so each keystroke does not scan every product.
    if (!searchValue) {
      setFilteredProductList(null);
      setFilteredLayoutList(null);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      const q = searchValue.toLowerCase();
      setFilteredProductList(
        productList.filter((product) => JSON.stringify(product).toLowerCase().includes(q))
      );
      setFilteredLayoutList(
        layoutList.filter((product) => JSON.stringify(product).toLowerCase().includes(q))
      );
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [searchValue, productList, layoutList]);

  // Function to sort products by different criteria
  const sortProducts = (products: IPopulatedProduct[]) => {
    if (sortOption === 'none') return [...products];
    if (sortOption === 'date-asc') return [...products].reverse(); // Oldest first (API order is newest first)

    return [...products].sort((a, b) => {
      if (sortOption === 'views-desc') {
        const viewsA = a.views || 0;
        const viewsB = b.views || 0;
        return viewsB - viewsA;
      } else if (sortOption === 'views-asc') {
        const viewsA = a.views || 0;
        const viewsB = b.views || 0;
        return viewsA - viewsB;
      }

      return 0;
    });
  };

  // Products: server company/tags + optional client search + favorites filter
  const productListBase = filteredProductList ?? productList;
  const productListForTab = showFavoritesOnly
    ? productListBase.filter((p) => p.starredByMe)
    : productListBase;
  // Layouts: no server tag filter; apply company filter client-side
  const applyCompanyFilter = (list: IPopulatedProduct[]) => {
    if (!selectedCompanies.length) return list;
    return list.filter((p) => {
      const name = p.owningUser_id?.companyName?.trim();
      return name && selectedCompanies.includes(name);
    });
  };
  const layoutListForTab = applyCompanyFilter(filteredLayoutList ?? layoutList);

  // Get sorted lists
  const sortedProductList = sortProducts(productListForTab);
  const sortedLayoutList = sortProducts(layoutListForTab);

  // Calculate actual list counts (after search + company filter)
  const productsCount = productListForTab.length;
  const layoutsCount = layoutListForTab.length;
  // Using 0 for tabs that don't have actual content yet
  const projectsCount = 0;
  const expertsCount = 0;

  const patchProductStar = useCallback((productId: string, state: ProductStarState) => {
    const patchList = (list: IPopulatedProduct[]) =>
      list.map((product) =>
        product._id === productId ? { ...product, ...state } : product
      );
    setProductList(patchList);
    setFilteredProductList((prev) => (prev ? patchList(prev) : null));
  }, []);

  // Use a fixed height container to stabilize all content
  return (
    <Center style={pageWrapper}>
      <Box
        style={{
          width: '80%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 120px)', // Fixed page height
        }}
      >
        <Box
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'var(--mantine-color-body)',
            paddingTop: 20,
            paddingBottom: 10,
            marginBottom: 20,
            boxShadow: 'none',
          }}
        >
          <PageHeader
            icon={<IconHome2 size={22} stroke={1.75} />}
            title={t('marketplace.header.title')}
            subtitle={t('marketplace.header.description')}
            accentColor={userProfile.qrCodeColor}
            mb={20}
          />

          <Group mb={10} align="flex-start" justify="space-between">
            <TextInput
              placeholder={t('marketplace.search.placeholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              size="sm"
              style={{ width: '100%' }}
            />
          </Group>

          <Tabs
            color={userProfile.qrCodeColor}
            variant="pills"
            defaultValue="products"
            style={getMarketplaceTabStyleVars(userProfile.qrCodeColor)}
            classNames={{ list: marketplaceTabStyles.list }}
            styles={{
              tabLabel: { width: '100%' },
              tab: {
                color: 'inherit',
              },
            }}
          >
            <Group justify="space-between" align="flex-start" mb={10} w="100%" wrap="wrap">
              <Tabs.List style={{ flexWrap: 'wrap', marginBottom: '10px' }}>
                <Tabs.Tab value="products" classNames={{ tab: marketplaceTabStyles.tab }}>
                  <MarketplaceTabLabel
                    icon={<IconPhoto size={16} stroke={1.75} />}
                    label={t('marketplace.entities.products')}
                    count={productsCount}
                    hideLabel={isMobile}
                  />
                </Tabs.Tab>
                <Tabs.Tab value="layouts" classNames={{ tab: marketplaceTabStyles.tab }}>
                  <MarketplaceTabLabel
                    icon={<IconUsers size={16} stroke={1.75} />}
                    label={t('marketplace.entities.layouts')}
                    count={layoutsCount}
                    hideLabel={isMobile}
                  />
                </Tabs.Tab>
                <Tabs.Tab value="projects" classNames={{ tab: marketplaceTabStyles.tab }}>
                  <MarketplaceTabLabel
                    icon={<IconCheckbox size={16} stroke={1.75} />}
                    label={t('marketplace.entities.projects')}
                    count={projectsCount}
                    hideLabel={isMobile}
                  />
                </Tabs.Tab>
                <Tabs.Tab value="experts" classNames={{ tab: marketplaceTabStyles.tab }}>
                  <MarketplaceTabLabel
                    icon={<IconStar size={16} stroke={1.75} />}
                    label={t('marketplace.entities.experts')}
                    count={expertsCount}
                    hideLabel={isMobile}
                  />
                </Tabs.Tab>
              </Tabs.List>

              <Group gap="xs" wrap="wrap" justify="flex-end">
                <MarketplaceToolbarButton
                  accentColor={userProfile.qrCodeColor}
                  active={showFavoritesOnly}
                  hideLabel={isMobile}
                  leftIcon={
                    showFavoritesOnly ? (
                      <IconStarFilled size={16} stroke={1.75} />
                    ) : (
                      <IconStar size={16} stroke={1.75} />
                    )
                  }
                  onClick={() => {
                    if (session.loading) return;
                    if (!session.doesSessionExist) {
                      notifications.show({
                        title: t('marketplace.search.favorites'),
                        message: t('marketplace.search.favoritesLoginRequired'),
                        color: 'yellow',
                      });
                      return;
                    }
                    setShowFavoritesOnly((prev) => !prev);
                  }}
                >
                  {t('marketplace.search.favorites')}
                </MarketplaceToolbarButton>
                <MarketplaceFiltersDropdown
                  companies={companyNames}
                  selectedCompanies={selectedCompanies}
                  onCompaniesChange={setSelectedCompanies}
                  tags={allTagsList}
                  tagCounts={tagCounts}
                  selectedTags={selectedTags}
                  onTagsChange={setSelectedTags}
                  // Temporarily hidden — categories UI disabled
                  // categories={allCategoriesList}
                  // categoryCounts={categoryCounts}
                  // selectedCategories={selectedCategories}
                  // onCategoriesChange={setSelectedCategories}
                  accentColor={userProfile.qrCodeColor}
                  isMobile={isMobile}
                />
                <Menu shadow="md" width={240} position="bottom-end" floatingStrategy="fixed">
                  <Menu.Target>
                    <MarketplaceToolbarButton
                      accentColor={userProfile.qrCodeColor}
                      active={sortOption !== 'none'}
                      hideLabel={isMobile}
                      leftIcon={<IconArrowsSort size={16} stroke={1.75} />}
                      rightIcon={<IconSelector size={15} stroke={1.75} />}
                    >
                      {t('marketplace.search.sortBy')}
                    </MarketplaceToolbarButton>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Label>{t('marketplace.search.sortBy')}</Menu.Label>
                    <Menu.Item
                      leftSection={<IconArrowsSort size={16} />}
                      rightSection={sortOption === 'none' ? <IconCheck size={14} /> : null}
                      onClick={() => setSortOption('none')}
                      style={
                        sortOption === 'none'
                          ? {
                              backgroundColor: `${userProfile.qrCodeColor}15`,
                              color: userProfile.qrCodeColor,
                              fontWeight: 600,
                            }
                          : {}
                      }
                    >
                      {t('marketplace.search.newest')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSortAscending size={16} />}
                      rightSection={sortOption === 'date-asc' ? <IconCheck size={14} /> : null}
                      onClick={() => setSortOption('date-asc')}
                      style={
                        sortOption === 'date-asc'
                          ? {
                              backgroundColor: `${userProfile.qrCodeColor}15`,
                              color: userProfile.qrCodeColor,
                              fontWeight: 600,
                            }
                          : {}
                      }
                    >
                      {t('marketplace.search.oldest')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSortDescending size={16} />}
                      rightSection={sortOption === 'views-desc' ? <IconCheck size={14} /> : null}
                      onClick={() => setSortOption('views-desc')}
                      style={
                        sortOption === 'views-desc'
                          ? {
                              backgroundColor: `${userProfile.qrCodeColor}15`,
                              color: userProfile.qrCodeColor,
                              fontWeight: 600,
                            }
                          : {}
                      }
                    >
                      {t('marketplace.search.popular')}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSortAscending size={16} />}
                      rightSection={sortOption === 'views-asc' ? <IconCheck size={14} /> : null}
                      onClick={() => setSortOption('views-asc')}
                      style={
                        sortOption === 'views-asc'
                          ? {
                              backgroundColor: `${userProfile.qrCodeColor}15`,
                              color: userProfile.qrCodeColor,
                              fontWeight: 600,
                            }
                          : {}
                      }
                    >
                      {t('marketplace.search.leastPopular')}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>

            <Tabs.Panel value="products" style={{ width: '100%' }}>
              <Text size="sm" c="dimmed" mb="md">
                {showFavoritesOnly
                  ? t('marketplace.descriptions.favorites')
                  : t('marketplace.descriptions.products')}
              </Text>
              {productsLoadState === 'loading' && productList.length === 0 ? (
                <Center py="xl">
                  <Loader color={userProfile.qrCodeColor} />
                </Center>
              ) : productsLoadState === 'error' && productList.length === 0 ? (
                <Alert
                  color="red"
                  variant="light"
                  radius="md"
                  icon={<IconAlertCircle size={18} />}
                  title={t('marketplace.loadErrorTitle')}
                >
                  <Text size="sm" mb="sm">
                    {t('marketplace.loadErrorMessage')}
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    color={userProfile.qrCodeColor}
                    leftSection={<IconRefresh size={14} />}
                    onClick={retryMarketplaceLoad}
                  >
                    {t('marketplace.retry')}
                  </Button>
                </Alert>
              ) : showFavoritesOnly && sortedProductList.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  {t('marketplace.search.favoritesEmpty')}
                </Text>
              ) : (
                <ProductLayoutGallery
                  layoutList={sortedProductList}
                  isLayout={false}
                  onStarChange={patchProductStar}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="layouts" style={{ width: '100%' }}>
              <Text size="sm" c="dimmed" mb="md">
                {t('marketplace.descriptions.layouts')}
              </Text>
              {layoutsLoadState === 'loading' && layoutList.length === 0 ? (
                <Center py="xl">
                  <Loader color={userProfile.qrCodeColor} />
                </Center>
              ) : layoutsLoadState === 'error' && layoutList.length === 0 ? (
                <Alert
                  color="red"
                  variant="light"
                  radius="md"
                  icon={<IconAlertCircle size={18} />}
                  title={t('marketplace.loadErrorTitle')}
                >
                  <Text size="sm" mb="sm">
                    {t('marketplace.loadErrorMessage')}
                  </Text>
                  <Button
                    size="xs"
                    variant="light"
                    color={userProfile.qrCodeColor}
                    leftSection={<IconRefresh size={14} />}
                    onClick={retryMarketplaceLoad}
                  >
                    {t('marketplace.retry')}
                  </Button>
                </Alert>
              ) : (
                <ProductLayoutGallery layoutList={sortedLayoutList} isLayout={true} />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="projects" style={{ width: '100%' }}>
              <MarketplaceComingSoonPanel
                icon={<IconCheckbox size={28} stroke={1.5} />}
                comingSoon={t('marketplace.descriptions.comingSoon')}
                hint={t('marketplace.descriptions.projects')}
                accentColor={userProfile.qrCodeColor}
              />
            </Tabs.Panel>

            <Tabs.Panel value="experts" style={{ width: '100%' }}>
              <MarketplaceComingSoonPanel
                icon={<IconStar size={28} stroke={1.5} />}
                comingSoon={t('marketplace.descriptions.comingSoon')}
                hint={t('marketplace.descriptions.experts')}
                accentColor={userProfile.qrCodeColor}
              />
            </Tabs.Panel>
          </Tabs>
        </Box>
      </Box>
    </Center>
  );
}
